import { Button, Icon } from "@mui/material"; // (se não usar, pode remover) 
import L, { CRS } from "leaflet";
import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import MapIcon from "@mui/icons-material/Map";
import CityIcon from "@mui/icons-material/LocationCity";
import HouseIcon from "@mui/icons-material/HouseOutlined";
import PinIcon from "@mui/icons-material/Grass";
import { BASE_URL, BASE_URL_GEO } from "../../services/common";

// --- nomes publicados no GeoServer (ajuste se forem diferentes) --- // <<<
const LAIR2021_LAYER = "lair:2021";  // sua camada 2021
const LAIR2021_STYLE = "uso_abc";            // SLD com a legenda

// ==================== PATCH ANTI-SEAM (aplicado cedo) ====================
let __antiSeamApplied = false;
function applyLeafletAntiSeamPatch() {
  if (__antiSeamApplied) return;
  __antiSeamApplied = true;

  const origInitTile = L.GridLayer.prototype._initTile;
  L.GridLayer.include({
    _initTile: function (tile) {
      origInitTile.call(this, tile);
      // overlap de 1px para cobrir a fresta entre tiles
      const size = this.getTileSize();
      tile.style.width = `${size.x + 1}px`;
      tile.style.height = `${size.y + 1}px`;
    },
  });
}
// aplica no carregamento do módulo (antes do MapContainer criar tiles)
applyLeafletAntiSeamPatch();

// ============================== COMPONENTE ===============================
export const MapaLeaflet = ({ codigoImovel, bbox, geom }) => {
  const [map, setMap] = useState(null);

  const [layerUf, setLayerUf] = useState(null);
  const [layerCity, setLayerCity] = useState(null);
  const [layerLair2021, setLayerLair2021] = useState(null);
  const [layerAreaImovel, setLayerAreaImovel] = useState(null);

  const [showLair2021, setShowLair2021] = useState(false);
  const [showLayerUf, setShowLayerUf] = useState(true);
  const [showLayerCity, setShowLayerCity] = useState(true);

  const [legendUrl2021, setLegendUrl2021] = useState(null);

  // -------- helpers: WKT -> GeoJSON (Polygon / MultiPolygon) --------
  const wktToGeoJSON = (wkt) => {
    if (!wkt || typeof wkt !== "string") return null;
    const s = wkt.trim();

    if (/^POLYGON\s*\(\(/i.test(s)) {
      const inner = s.replace(/^POLYGON\s*\(\(/i, "").replace(/\)\)\s*$/, "");
      const rings = inner.split("),(");
      const coordinates = rings.map((ring) =>
        ring.split(",").map((pair) => {
          const [x, y] = pair.trim().split(/\s+/).map(Number);
          return [x, y]; // [lon, lat]
        })
      );
      return { type: "Polygon", coordinates: [coordinates[0], ...coordinates.slice(1)] };
    }

    if (/^MULTIPOLYGON\s*\(\(\(/i.test(s)) {
      const inner = s.replace(/^MULTIPOLYGON\s*\(\(\(/i, "").replace(/\)\)\)\s*$/, "");
      const polys = inner.split(")),((");
      const coordinates = polys.map((poly) => {
        const rings = poly.split("),(");
        const ringCoords = rings.map((ring) =>
          ring.split(",").map((pair) => {
            const [x, y] = pair.trim().split(/\s+/).map(Number);
            return [x, y];
          })
        );
        return [ringCoords[0], ...ringCoords.slice(1)];
      });
      return { type: "MultiPolygon", coordinates };
    }

    return null;
  };

  // Normaliza a geometria recebida: aceita GeoJSON ou WKT
  const geojson = useMemo(() => {
    if (!geom) return null;
    if (typeof geom === "string") return wktToGeoJSON(geom);
    if (geom.type && geom.coordinates) return geom;
    return null;
  }, [geom]);

  // Cria camadas WMS base e mantém referências
  useEffect(() => {
    const uf = buildBaseLayer("lair:uf", "ufs");
    const mun = buildBaseLayer("lair:municipio", "municipio_branco");
    const lair2021 = buildLayerImovel(LAIR2021_LAYER, LAIR2021_STYLE); // <<<

    setLayerUf(uf);
    setLayerCity(mun);
    setLayerLair2021(lair2021);
  }, []); // uma vez

  // Toggle LAIR 2021
  useEffect(() => {
    if (!map || !layerLair2021) return;
    showLair2021 ? layerLair2021.addTo(map) : map.removeLayer(layerLair2021);
  }, [map, showLair2021, layerLair2021]);

  // legenda do 2021 só quando visível // <<<
  useEffect(() => {
    if (showLair2021) {
      setLegendUrl2021(getLegendGraphicUrl(LAIR2021_LAYER, LAIR2021_STYLE));
    } else {
      setLegendUrl2021(null);
    }
  }, [showLair2021]);

  // Toggle Municípios
  useEffect(() => {
    if (!map || !layerCity) return;
    showLayerCity ? layerCity.addTo(map) : map.removeLayer(layerCity);
  }, [map, showLayerCity, layerCity]);

  // Toggle UF
  useEffect(() => {
    if (!map || !layerUf) return;
    showLayerUf ? layerUf.addTo(map) : map.removeLayer(layerUf);
  }, [map, showLayerUf, layerUf]);

  // Layer da área do imóvel (WMS) quando muda o código
  useEffect(() => {
    if (!map || !codigoImovel) return;

    if (layerAreaImovel) {
      try { map.removeLayer(layerAreaImovel); } catch (_) {}
    }

    const lairArea = buildLayerImovel("lair:area_imovel"); // style indefinido aqui (ok)
    lairArea.addTo(map);
    setLayerAreaImovel(lairArea);
  }, [map, codigoImovel]); // eslint-disable-line react-hooks/exhaustive-deps

  // FitBounds quando o bbox mudar
  useEffect(() => {
    if (!map || !bbox || !Array.isArray(bbox) || bbox.length !== 2) return;
    try {
      map.fitBounds(bbox, { padding: [20, 20] });
    } catch (_) {}
  }, [map, bbox]);

  // Se não houver bbox, tenta ajustar pelo próprio GeoJSON
  useEffect(() => {
    if (!map || bbox || !geojson) return;
    const collect = [];
    const push = ([lng, lat]) => collect.push([lat, lng]);

    if (geojson.type === "Polygon") {
      geojson.coordinates.forEach((ring) => ring.forEach(push));
    } else if (geojson.type === "MultiPolygon") {
      geojson.coordinates.forEach((poly) => poly.forEach((ring) => ring.forEach(push)));
    }

    if (collect.length) {
      const lats = collect.map((c) => c[0]);
      const lngs = collect.map((c) => c[1]);
      const sw = [Math.min(...lats), Math.min(...lngs)];
      const ne = [Math.max(...lats), Math.max(...lngs)];
      map.fitBounds([sw, ne], { padding: [20, 20] });
    }
  }, [map, bbox, geojson]);

  // --------- Builders / helpers WMS ----------
  const buildLayerImovel = (layer, style) => { // <<< aceitando style opcional
    const base = BASE_URL_GEO.replace(/\/+$/, "");
    return L.tileLayer.wms(`${base}/geoserver/lair/wms`, {
      layers: layer,
      styles: style,                 // <<<
      format: "image/png",
      transparent: true,
      version: "1.1.0",
      crs: CRS.EPSG900913, // EPSG:3857
      cql_filter: `cod_imovel='${String(codigoImovel).replace(/'/g, "''")}'`,
      zIndex: 400,
    });
  };

  const buildBaseLayerYoux = (layer, service = "gwc/service/wms") => {
    const base = BASE_URL_GEO.replace(/\/+$/, "");
    const svc = service.replace(/^\/+/, "");
    const url = `${base}/geoserver/${svc}`;
    return L.tileLayer.wms(url, {
      layers: layer,
      format: "image/png",
      transparent: true,
      version: "1.1.0",
      crs: CRS.EPSG900913,
      zIndex: 200,
    });
  };

  const buildBaseLayer = (layer, styles = null) => {
    const base = BASE_URL_GEO.replace(/\/+$/, "");
    return L.tileLayer.wms(`${base}/geoserver/lair/wms`, {
      layers: layer,
      format: "image/png",
      transparent: true,
      version: "1.1.0",
      crs: CRS.EPSG900913,
      styles,
      zIndex: 300,
    });
  };

  // URL do GetLegendGraphic para a legenda do 2021 // <<<
  const getLegendGraphicUrl = (layer, style) => {
    const base = BASE_URL_GEO.replace(/\/+$/, "");
    const u = new URL(`${base}/geoserver/lair/wms`);
    u.searchParams.set("service", "WMS");
    u.searchParams.set("request", "GetLegendGraphic");
    u.searchParams.set("layer", layer);
    u.searchParams.set("style", style);
    u.searchParams.set("format", "image/png");
    u.searchParams.set("transparent", "true");
    // opções úteis (tamanho/fonte/hinting)
    u.searchParams.set("legend_options", "fontName:Arial;fontAntiAliasing:true;fontSize:12;forceLabels:on");
    // cache-busting simples
    u.searchParams.set("_", String(Date.now()));
    return u.toString();
  };

  return (
    <div style={{ position: "relative", overflow: "hidden" }}>
      <MapContainer
        ref={setMap}
        attributionControl={false}
        zoomSnap={1}
        style={{ height: "300px", width: "auto" }}
        id="map"
      >
        {/* Controles customizados */}
        <div>
          <div
            style={controlStyle(80)}
            className="leaflet-control toggle-add-layers"
            onClick={() => setShowLayerUf((prev) => !prev)}
            title="Adicionar camadas de estados"
          >
            <MapIcon style={{ color: "#024634" }} />
          </div>
          <div
            style={controlStyle(5)}
            className="leaflet-control toggle-add-layers"
            onClick={() => setShowLayerCity((prev) => !prev)}
            title="Adicionar camadas de cidades"
          >
            <CityIcon style={{ color: "#024634" }} />
          </div>
          <div
            style={controlStyle(5)}
            className="leaflet-control toggle-add-layers"
            onClick={() => setShowLair2021((prev) => !prev)}
            title="Adicionar camadas de usos"
          >
            <PinIcon style={{ color: "#024634" }} />
          </div>
          <div
            style={controlStyle(5)}
            className="leaflet-control toggle-add-layers"
            onClick={() => map?.fitBounds(bbox)}
            title="Encontrar Imóvel"
          >
            <HouseIcon style={{ color: "#024634" }} />
          </div>
        </div>

        {/* Base: Esri World Imagery sem seams */}
        <TileLayer
          url="https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          tileSize={512}
          zoomOffset={-1}
          detectRetina={false}
          maxNativeZoom={19}
          updateWhenZooming={false}
          updateWhenIdle={true}
          keepBuffer={2}
          opacity={1}
          className="no-seams"
          zIndex={100}
        />

        {/* Polígono do imóvel (front) */}
        {geojson && (
          <GeoJSON
            data={geojson}
            style={() => ({
              color: "#00bcd4",
              weight: 3,
              opacity: 1,
              fillColor: "#00bcd4",
              fillOpacity: 0.15,
            })}
            onEachFeature={(_, layer) => {
              if (codigoImovel) {
                layer.bindTooltip(String(codigoImovel), { sticky: true });
              }
            }}
          />
        )}
      </MapContainer>

      {/* Legenda do 2021 (aparece só quando a camada está ligada) */} {/* <<< */}
      {legendUrl2021 && (
        <div
          className="leaflet-control"
          style={{
            position: "absolute",
            right: 8,
            bottom: 8,
            background: "rgba(255,255,255,0.9)",
            padding: 8,
            borderRadius: 6,
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            pointerEvents: "none",
            zIndex: 1100, // <- acima dos panes e junto dos controles
            maxWidth: 180,
          }}
        >
          <img
            src={legendUrl2021}
            alt="Legenda uso 2021"
            style={{ maxHeight: 160 }}
          />
          <div style={{ fontSize: 10, color: "#444", textAlign: "center", marginTop: 4 }}>
            Parâmetros da legenda são fixos devido a limitações do GeoServer.
          </div>
        </div>
      )}
    </div>
  );
};

// Estilo dos botões flutuantes
function controlStyle(marginTop) {
  return {
    backgroundColor: "white",
    marginTop,
    marginLeft: 12,
    width: 30,
    height: 30,
    borderRadius: 3,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
  };
}
